import React, { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useMutation } from '@apollo/client';

import { GetGuideQuery } from '../__generated__/graphql';
import { Unpacked } from '../graphQLTypes/types';
import { StyledPaper } from '../theme';
import { ConfirmDialog, ConfirmDialogProps } from '../dialogs/ConfirmDialog';
import { useModal } from '../dialogs/useModal';
import { RichText, RichTextField, htmlIsEmpty } from '../components/richText';
import { ADD_CITATION, REMOVE_CITATION, UPDATE_CITATION } from './guide.graphql';

type Citation = NonNullable<Unpacked<NonNullable<GetGuideQuery['guide']>['citations']>>;

export function Citations({ guideId, citations, elevation }: { guideId: string; citations: Citation[]; elevation?: number }) {
  const [addCitation, { loading: adding }] = useMutation(ADD_CITATION);
  const [updateCitation] = useMutation(UPDATE_CITATION);
  const [removeCitation] = useMutation(REMOVE_CITATION);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newHtml, setNewHtml] = useState('');
  const confirmDeleteModal = useModal<ConfirmDialogProps, boolean>({
    data: {
      title: 'Slett litteraturhenvisning',
      message: 'Er du sikker på at du vil slette litteraturhenvisningen? Referanser i innholdet som peker på henvisningen blir brutt.',
      submitText: 'Slett',
      cancelText: 'Avbryt',
    },
  });

  const handleDelete = async (citationId: string) => {
    const confirmed = await confirmDeleteModal.open();
    if (!confirmed) return;
    await removeCitation({ variables: { input: { id: guideId, citationId } } });
  };

  const openAdd = () => {
    setNewHtml('');
    setAddOpen(true);
  };

  const closeAdd = () => {
    if (adding) return;
    setAddOpen(false);
  };

  const onAdd = async () => {
    if (htmlIsEmpty(newHtml)) return;
    await addCitation({ variables: { input: { id: guideId, text: newHtml } } });
    setNewHtml('');
    setAddOpen(false);
  };

  return (
    <StyledPaper elevation={elevation}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" component="h3">
          Litteraturhenvisninger
        </Typography>
        <IconButton aria-label="Legg til litteraturhenvisning" onClick={openAdd}>
          <Add />
        </IconButton>
      </Stack>
      {citations.length === 0 ? (
        <Typography variant="subtitle1" color="textSecondary">
          Ingen angitt
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {citations.map((citation) => (
              <CitationRow
                key={citation.id}
                citation={citation}
                guideId={guideId}
                isEditing={editingId === citation.id}
                onStartEdit={() => setEditingId(citation.id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={async (text) => {
                  await updateCitation({ variables: { input: { id: guideId, citationId: citation.id, text } } });
                  setEditingId(null);
                }}
                onDelete={() => handleDelete(citation.id)}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={addOpen} onClose={closeAdd} fullWidth maxWidth="sm" disableRestoreFocus>
        <DialogTitle>Legg til litteraturhenvisning</DialogTitle>
        <DialogContent>
          <RichTextField value={newHtml} onChange={setNewHtml} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} disabled={adding}>
            Avbryt
          </Button>
          <Button onClick={onAdd} disabled={adding || htmlIsEmpty(newHtml)} variant="contained">
            Legg til
          </Button>
        </DialogActions>
      </Dialog>
      {confirmDeleteModal.isOpen && <ConfirmDialog modal={confirmDeleteModal} />}
    </StyledPaper>
  );
}

interface CitationRowProps {
  citation: Citation;
  guideId: string;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (html: string) => Promise<void>;
  onDelete: () => void;
}

function CitationRow({ citation, isEditing, onStartEdit, onCancelEdit, onSave, onDelete }: CitationRowProps) {
  const [draft, setDraft] = useState(citation.text);

  useEffect(() => {
    if (isEditing) setDraft(citation.text);
  }, [isEditing, citation.text]);

  return (
    <TableRow hover>
      <TableCell sx={{ verticalAlign: 'top' }}>
        {isEditing ? (
          <Stack spacing={1}>
            <RichTextField value={draft} onChange={setDraft} autoFocus />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={onCancelEdit}>
                Avbryt
              </Button>
              <Button size="small" variant="contained" onClick={() => onSave(draft)} disabled={htmlIsEmpty(draft)}>
                Lagre
              </Button>
            </Stack>
          </Stack>
        ) : (
          <div onClick={onStartEdit} style={{ cursor: 'text', minHeight: 24 }}>
            <RichText html={citation.text} component="div" />
          </div>
        )}
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {!isEditing && (
          <IconButton aria-label="Rediger litteraturhenvisning" onClick={onStartEdit}>
            <Edit />
          </IconButton>
        )}
        <IconButton aria-label="Fjern litteraturhenvisning" onClick={onDelete}>
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
